import React from 'react'
import { ConciergeMode } from '../types/communication.types'
import { 
  CONCIERGE_MODE_LABELS, 
  CONCIERGE_MODE_ICONS,
  CONCIERGE_MODE_COLORS 
} from '../services/conciergeModeService'

interface ConciergeModeBadgeProps {
  mode: ConciergeMode
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
  showLabel?: boolean
  animated?: boolean
  className?: string
}

/**
 * Badge visual para indicar o modo ativo do Concierge
 * Exibe cores e ícones diferentes para cada modo
 */
export const ConciergeModeBadge: React.FC<ConciergeModeBadgeProps> = ({
  mode,
  size = 'md',
  showIcon = true,
  showLabel = true,
  animated = true,
  className = ''
}) => {
  const label = CONCIERGE_MODE_LABELS[mode]
  const icon = CONCIERGE_MODE_ICONS[mode]
  const color = CONCIERGE_MODE_COLORS[mode]

  const sizeClasses = {
    sm: 'badge-sm fs-8',
    md: 'fs-7',
    lg: 'badge-lg fs-6'
  }

  const badgeColorClass = {
    manual: 'badge-light-primary',
    assisted: 'badge-light-warning',
    autopilot: 'badge-light-success'
  }

  const pulseAnimation = animated && mode === 'autopilot'

  return (
    <span 
      className={`badge ${badgeColorClass[mode]} ${sizeClasses[size]} ${className}`}
      style={{ 
        borderLeft: `4px solid ${color}`,
        position: 'relative',
        ...(pulseAnimation && {
          animation: 'pulse 2s infinite'
        })
      }}
      title={`Modo: ${label}`}
    >
      {showIcon && <i className={`fa ${icon} me-1`}></i>}
      {showLabel && label}
      
      {/* Badge extra para autopilot */}
      {mode === 'autopilot' && (
        <span 
          className='ms-2 badge badge-danger badge-sm'
          style={{ 
            fontSize: '0.65rem',
            padding: '2px 6px'
          }}
        >
          AUTO
        </span>
      )}

      {/* Indicador pulsante para autopilot */}
      {pulseAnimation && (
        <span 
          className='position-absolute top-0 start-100 translate-middle p-1 bg-success border border-light rounded-circle'
          style={{
            animation: 'blink 1.5s infinite'
          }}
        >
          <span className='visually-hidden'>Modo automático ativo</span>
        </span>
      )}

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }

          @keyframes blink {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.3;
            }
          }
        `}
      </style>
    </span>
  )
}

/**
 * Badge compacto para exibir apenas o ícone (para uso em listas)
 */
export const ConciergeModeBadgeCompact: React.FC<{
  mode: ConciergeMode
  size?: 'sm' | 'md' | 'lg'
}> = ({ mode, size = 'sm' }) => {
  return (
    <ConciergeModeBadge
      mode={mode}
      size={size}
      showLabel={false}
      showIcon={true}
      animated={false}
    />
  )
}
